import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, Navigate, Outlet, useSearchParams } from 'react-router-dom';

import { RootState } from '../ducks/store';
import { isPlatformAdmin, isTeamManager } from '../utils/functions';
import NavRail from './common/NavColumn/NavRail';
import OutletWrapper from './common/OutletWrapper/OutletWrapper';
import { showModal } from '../ducks/modal';
import MaintenanceNoticeModal from './common/modals/MaintenanceNoticeModal/MaintenanceNoticeModal';
import { ModalTypes } from '../utils/constants';
import InactivityModal from './common/modals/InactivityModal/InactivityModal';
import { useCookieConsent } from '../hooks/useCookieConsent';

const win: IWindow = window;

const useHubSpotChat = (isAuthenticated: boolean, hasAnalyticsConsent: boolean) => {
  useEffect(() => {
    // this is meant to add close icon to Hubspot Chat. This useEffect ties
    // into the window MessageEvent handler and adds it when the
    // widget is initialized
    const onMessage = (e: MessageEvent<any>) => {
      let data;
      try {
        // Attempt to parse strings as JSON
        data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
      } catch (ex) {
        if (!data && e.data) {
          data = e.data;
        }
      }
    };

    // If the user is authenticated, inject the Hubspot script
    if (isAuthenticated) {
      // Check if consent has been accepted for analytics cookies,
      // otherwise, don't load the HubSpot script because it will display
      // a poorly styled and positioned tooltip in the bottom right corner
      // stating that consent is required.

      if (hasAnalyticsConsent) {
        if (win.HubSpotConversations?.widget) {
          console.info('HubSpot widget already exists, refreshing ...');
          win.HubSpotConversations.widget.refresh();
        } else {
          console.info('Adding HubSpot widget');
          const script = document.createElement('script');
          script.src = '//js.hs-scripts.com/5653847.js';
          script.async = true;
          script.id = 'hs-script-loader';
          script.type = 'text/javascript';
          script.defer = true;
          const root = document.getElementById('root');
          root?.appendChild(script);

          win.addEventListener('message', onMessage);
        }
      } else {
        // Clean up the HubSpot widget if it exists
        console.info('Removing HubSpot widget due to lack of analytics consent');
        win.HubSpotConversations?.widget?.remove();
      }
    }
    // We only want to run this on load, so the empty deps is fine
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAnalyticsConsent]);
};

const RequireAuth = ({
  children,
  isSettings,
  checkManager,
  checkAdmin,
}: {
  children?: JSX.Element;
  isSettings?: boolean;
  checkManager?: boolean;
  checkAdmin?: boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    isAuthenticated,
    isAdmin,
    isManager,
    forcePasswordReset,
    forceRegistration,
    unmetRequirements,
    modalType,
    maintenanceNotified,
    userId,
  } = useSelector((state: RootState) => {
    const { isAuthenticated, authenticatedUser, maintenanceNotified } = state.auth;
    const { showType } = state.modal;
    const isAdmin = isPlatformAdmin(authenticatedUser);
    const isManager = isTeamManager(authenticatedUser);
    return {
      isAuthenticated,
      isAdmin,
      isManager,
      forcePasswordReset: authenticatedUser?.forcePasswordReset,
      forceRegistration: authenticatedUser?.forceRegistration,
      unmetRequirements: authenticatedUser?.unmetRequirements,
      modalType: showType,
      maintenanceNotified,
      userId: authenticatedUser?._id,
    };
  });

  const { hasAnalyticsConsent } = useCookieConsent();
  useHubSpotChat(!!isAuthenticated, hasAnalyticsConsent);

  useEffect(() => {
    if (isAuthenticated && userId) {
      searchParams.set('uid', userId);
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, isAuthenticated, userId]);

  if (isAuthenticated) {
    if ((forcePasswordReset || forceRegistration || unmetRequirements) && !isSettings) {
      return <Navigate to="/settings" state={{ from: location }} replace />;
    } else if (!checkAdmin || isAdmin) {
      if (!isAdmin) {
        return (
          <>
            {children || <Outlet />}
            <MaintenanceNoticeModal
              show={
                modalType === ModalTypes.MaintenanceCountdown ||
                (modalType === ModalTypes.Maintenance && !maintenanceNotified)
              }
              onHide={() => dispatch(showModal())}
            />
            <InactivityModal
              show={modalType === ModalTypes.Inactivity}
              onHide={() => dispatch(showModal())}
            />
          </>
        );
      }

      return (
        <>
          <NavRail />
          <OutletWrapper>{children || <Outlet />}</OutletWrapper>
        </>
      );
    } else if ((checkAdmin && !isAdmin) || (checkManager && !isManager)) {
      return <Navigate to="/" state={{ from: location }} replace />;
    }
  }
  return <Navigate to={`/login${location.search}`} state={{ from: location }} replace />;
};

export default RequireAuth;
